import React, { useState } from 'react';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';

interface AppleSignInButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
}

export function AppleSignInButton({ onSuccess, onError, className = '' }: AppleSignInButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleAppleSignIn = async () => {
    setLoading(true);

    try {
      logger.info('Apple Sign-In initiated', {
        currentUrl: window.location.href,
        redirectTo: `${window.location.origin}/auth/verify`
      });

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `https://wayja.me/auth/verify`
        }
      });

      if (error) {
        logger.error('Apple Sign-In error:', error);
        console.error('Apple Sign-In error details:', error);

        // Provide more specific error messages
        let errorMessage = 'Failed to sign in with Apple';

        if (error.message?.includes('invalid_client')) {
          errorMessage = 'Apple Sign-In is not properly configured. Please contact support.';
        } else if (error.message?.includes('redirect_uri_mismatch')) {
          errorMessage = 'Redirect URL mismatch. Please try again or contact support.';
        } else if (error.message) {
          errorMessage = error.message;
        }

        alert(errorMessage);
        onError?.(errorMessage);
        return;
      }

      if (data) {
        logger.info('Apple Sign-In OAuth initiated successfully:', {
          url: data.url,
          provider: data.provider
        });
        console.log('Apple Sign-In data:', data);
        onSuccess?.();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      logger.error('Apple Sign-In unexpected error:', err);
      console.error('Apple Sign-In unexpected error:', err);
      alert(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleAppleSignIn}
      disabled={loading}
      className={`
        w-full flex items-center justify-center gap-3 px-4 py-2 
        bg-black text-white rounded-md font-medium
        hover:bg-gray-800 transition-colors duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
    >
      {/* Apple Logo SVG */}
      <svg 
        className="w-5 h-5" 
        viewBox="0 0 24 24" 
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>

      <span>
        {loading ? 'Signing in...' : 'Sign in with Apple'}
      </span>
    </button>
  );
}
